import { z } from 'zod';

/**
 * Schemas Zod da fronteira C4 do fluxo de autenticação (register, códigos de senha e
 * GET /auth/me) — tolerantes na borda (WhenWritingNull): campo ausente tem default
 * explícito ou vira null no tipo resultante, nunca `undefined` dentro de componente.
 * Formatos reais da tech-api (TechAuthDtos.cs / TechAuthEndpoints.cs).
 */

/** GET /auth/me — vínculo de domínio da sessão (TechAuthMeResponse). */
export interface TechAuthMe {
  readonly id: string;
  readonly nome: string;
  readonly email: string;
  readonly phone: string | null;
  readonly externalUserId: string | null;
  readonly ativo: boolean;
  readonly ultimoLogin: string | null;
}

const MeSchema = z.object({
  id: z.string().uuid(),
  nome: z.string().min(1),
  email: z.string().email(),
  phone: z.string().nullish(),
  externalUserId: z.string().nullish(),
  ativo: z.boolean(),
  ultimoLogin: z.string().nullish(),
});

export function parseMe(payload: unknown): TechAuthMe {
  const parsed = MeSchema.parse(payload);
  return {
    id: parsed.id,
    nome: parsed.nome,
    email: parsed.email,
    phone: parsed.phone ?? null,
    externalUserId: parsed.externalUserId ?? null,
    ativo: parsed.ativo,
    ultimoLogin: parsed.ultimoLogin ?? null,
  };
}

/** POST /auth/register — resposta 200 (TechRegisterResponse). */
export interface RegisterResponse {
  readonly id: string;
  readonly externalUserId: string;
  readonly onboardingStatusId: number;
  readonly sourceId: number;
}

const RegisterResponseSchema = z.object({
  id: z.string().min(1),
  externalUserId: z.string().min(1),
  // Tolerante: campos aditivos podem vir ausentes (WhenWritingNull) — default seguro 0.
  onboardingStatusId: z.number().default(0),
  sourceId: z.number().default(0),
});

export function parseRegisterResponse(payload: unknown): RegisterResponse {
  return RegisterResponseSchema.parse(payload);
}

/** POST /auth/password/forgot e /setup/resend — sempre 200 (anti-enumeração no backend). */
export function parsePasswordCodeResponse(payload: unknown): string {
  return z.object({ message: z.string() }).parse(payload).message;
}

/** GET /equipment-categories — catálogo de especialidades do cadastro. */
export interface EquipmentCategoryOption {
  readonly id: number;
  readonly code: string;
  readonly name: string;
}

const EquipmentCategoryOptionSchema = z.object({
  id: z.union([z.number(), z.string()]),
  code: z.string(),
  name: z.string(),
});

export function parseEquipmentCategories(payload: unknown): EquipmentCategoryOption[] {
  const parsed = z.array(EquipmentCategoryOptionSchema).parse(payload);
  return parsed.map((item) => ({
    id: typeof item.id === 'number' ? item.id : Number(item.id),
    code: item.code,
    name: item.name,
  }));
}
